import { ExportUIModel } from '@y42/data-export';
import { DataLineageDiagramUIModel, DataLineageNodeBinding, DataLineageUINode } from '@y42/data-lineage/diagram';
import { DatasourceTableUIModel, DatasourceUIModel } from '@y42/datasource';
import {
  AbstractFeatureChildUIModel,
  AbstractFeatureUIModel,
  Y42FeatureTypeToChildLogo,
  Y42FeatureTypeToLogo,
} from '@y42/feature';
import { IntegrationUIModel } from '@y42/integration';
import {
  CanvasElementType,
  CanvasWidgetSettings,
  DataLineageLink,
  DataLineageNodeTypes,
  JobStatus,
  Y42FeatureType,
} from '@y42/json-schema';
import { SqlModelingUIModel } from '@y42/sql-model';
import { UiModelingUIModel } from '@y42/ui-model';
import { VisualizationDashboardUIModel } from '@y42/visualization/dashboard';
import { VisualizationWidgetUIModel } from '@y42/visualization/widget';
import { findSqlTableInputs } from '../../../modeling/creators/sql-modeling-creator/utils/find-sql-model-table-inputs';
import { findInputTables } from '../../../modeling/creators/ui-modeling-creator/utils/find-model-table-inputs';

/**
 * Unsaved Lineage includes the uncommitted nodes in the lineage. It is used to generate Ref Lineage for Bottom Drawer.
 * @param integrations
 * @param uiModels
 * @param sqlModels
 * @param dashboards
 * @param widgets
 * @param exports
 * @returns
 */
export const generateUnsavedLineage = async (
  integrations: IntegrationUIModel[],
  uiModels: UiModelingUIModel[],
  sqlModels: SqlModelingUIModel[],
  dashboards: VisualizationDashboardUIModel[],
  widgets: VisualizationWidgetUIModel[],
  exports: ExportUIModel[],
): Promise<DataLineageDiagramUIModel> => {
  const integrationNodes = generateDatasourceNodes(
    integrations,
    DataLineageNodeTypes.Integration,
    Y42FeatureType.Integration,
  );

  const uiModelNodes = generateDatasourceNodes(uiModels, DataLineageNodeTypes.UiModel, Y42FeatureType.UiModel);
  const uiModelLinks = await generateUiModelLinks(uiModels);

  const sqlModelNodes = generateDatasourceNodes(sqlModels, DataLineageNodeTypes.SqlModel, Y42FeatureType.SqlModel);
  const sqlModelLinks = await generateSqlModelLinks(sqlModels);

  const exportNodes = generateExportNodes(exports);
  const exportLinks = generateExportLinks(exports);

  const widgetNodes = generateWidgetNodes(widgets);
  const widgetLinks = generateWigetLinks(widgets);

  const dashboardNodes = generateDashboardNodes(dashboards);
  const dashboardLinks = generateDashboardLinks(dashboards);

  return {
    linkDataArray: [...uiModelLinks, ...sqlModelLinks, ...exportLinks, ...widgetLinks, ...dashboardLinks],
    nodeDataArray: [
      ...integrationNodes,
      ...uiModelNodes,
      ...sqlModelNodes,
      ...exportNodes,
      ...widgetNodes,
      ...dashboardNodes,
    ],
    visualDataArray: [],
  };
};

const generateDatasourceNodes = <T extends DatasourceUIModel = DatasourceUIModel>(
  datasources: T[],
  nodeType: DataLineageNodeTypes,
  featureType: Y42FeatureType,
) => {
  const tables = datasources.map((datasource) => datasource.tables).flat();
  const tableNodes = tables.map((table) =>
    getDataLineageUiNode(table, {
      category: nodeType,
      logo: Y42FeatureTypeToChildLogo[featureType],
      isGroup: false,
      group: table.$parent,
      jobStatus: table.latest_job?.status,
      validColumnsCount: table.latest_job?.columns.length,
    }),
  );

  const datasourceNodes = datasources.map((datasource) =>
    getDataLineageUiNode(datasource, {
      category: nodeType,
      logo: datasource?.typeModel?.logo,
      itemType: datasource?.typeModel?.type,
      isGroup: true,
    }),
  );

  return [...datasourceNodes, ...tableNodes];
};

const generateExportNodes = (exports: ExportUIModel[]) => {
  const triggers = exports.map((exportItem) => exportItem.triggers).flat();
  const triggerNodes = triggers.map((trigger) =>
    getDataLineageUiNode(trigger, {
      category: DataLineageNodeTypes.Export,
      logo: Y42FeatureTypeToChildLogo[Y42FeatureType.Export],
      isGroup: false,
      group: trigger.$parent,
      jobStatus: trigger.latest_job?.status,
    }),
  );

  const exportNodes = exports.map((exportItem) =>
    getDataLineageUiNode(exportItem, {
      category: DataLineageNodeTypes.Export,
      logo: exportItem?.typeModel?.logo,
      isGroup: true,
    }),
  );

  return [...exportNodes, ...triggerNodes];
};

const generateWidgetNodes = (widgets: VisualizationWidgetUIModel[]) => {
  const widgetNodes = widgets.map((widget) =>
    getDataLineageUiNode(widget, {
      category: DataLineageNodeTypes.Widget,
      logo: Y42FeatureTypeToLogo[Y42FeatureType.VisualizationWidget],
      isGroup: false,
    }),
  );

  return widgetNodes;
};

const generateDashboardNodes = (dashboards: VisualizationDashboardUIModel[]) => {
  const tabs = dashboards
    .map((dashboard) => dashboard.tabs)
    .flat()
    .filter((tab) => !!tab?.$path);
  const tabNodes = tabs.map((tab) =>
    getDataLineageUiNode(tab, {
      category: DataLineageNodeTypes.Dashboard,
      logo: Y42FeatureTypeToChildLogo[Y42FeatureType.VisualizationDashboard],
      isGroup: false,
      group: tab.$parent,
    }),
  );

  const dashboardNodes = dashboards.map((dashboard) =>
    getDataLineageUiNode(dashboard, {
      category: DataLineageNodeTypes.Dashboard,
      logo: Y42FeatureTypeToChildLogo[Y42FeatureType.VisualizationDashboard],
      isGroup: true,
    }),
  );

  return [...dashboardNodes, ...tabNodes];
};

// Links
const generateUiModelLinks = async (uiModels: UiModelingUIModel[]) => {
  const tableInputs: Array<{ table: DatasourceTableUIModel; inputs: string[] }> = [];

  for (const uiModel of uiModels) {
    const gojsModel = await uiModel.getUiModel?.();
    for (const table of uiModel.tables) {
      if (gojsModel) {
        const inputs = findInputTables(table.$name, gojsModel);
        tableInputs.push({ table, inputs });
      }
    }
  }

  return getInputLinks(tableInputs);
};

const generateSqlModelLinks = async (sqlModels: SqlModelingUIModel[]) => {
  const tableInputs: Array<{ table: DatasourceTableUIModel; inputs: string[] }> = [];

  for (const model of sqlModels) {
    const sqlModel = await model.getSqlModel?.();
    for (const table of model.tables) {
      const tableSql = sqlModel?.find((sm) => sm.table_name === table.$name)?.sql;
      if (tableSql) {
        const inputs = findSqlTableInputs(tableSql);
        tableInputs.push({ table, inputs });
      }
    }
  }

  return getInputLinks(tableInputs);
};

const getInputLinks = (tableInputs: Array<{ table: DatasourceTableUIModel; inputs: string[] }>) => {
  const links: DataLineageLink[] = [];
  for (const tableInput of tableInputs) {
    for (const input of tableInput.inputs) {
      links.push({ from: input, to: tableInput.table.$path });
    }
  }

  return links;
};

const generateExportLinks = (exports: ExportUIModel[]) => {
  const links: DataLineageLink[] = [];

  exports.forEach((e) => {
    e.triggers.forEach((trigger) => {
      if (trigger.settings?.datasource_table_path) {
        links.push({ from: trigger.settings.datasource_table_path, to: trigger.$path });
      }
    });
  });

  return links;
};

const generateWigetLinks = (widgets: VisualizationWidgetUIModel[]): DataLineageLink[] => {
  return widgets
    .filter((w) => !!w.datasource_table_path)
    .map((widget) => ({ from: widget.datasource_table_path!, to: widget.$path }));
};

const generateDashboardLinks = (dashboards: VisualizationDashboardUIModel[]) => {
  const links: DataLineageLink[] = [];

  const tabs = dashboards.map((d) => d.tabs).flat();
  tabs.forEach((tab) => {
    (tab.canvasElements ?? [])
      .filter((canvasElem) => canvasElem.type === CanvasElementType.Widget)
      .forEach((widget) => {
        if (widget.typeSettings) {
          links.push({
            from: (widget.typeSettings as CanvasWidgetSettings).widgetPath,
            to: tab.$path,
          });
        }
      });
  });

  return links;
};

const getDataLineageUiNode = <T extends AbstractFeatureUIModel | AbstractFeatureChildUIModel>(
  item: T,
  config: {
    category: DataLineageNodeTypes;
    logo?: string;
    isGroup?: boolean;
    group?: string;
    jobStatus?: JobStatus;
    validColumnsCount?: number;
    itemType?: string;
  },
) => {
  const node: DataLineageUINode = {
    key: item.$path,
    isGroup: config.isGroup,
    group: config.group,
    category: config.category,
    [DataLineageNodeBinding.Name]: item.name,
    [DataLineageNodeBinding.$Name]: item.$name,
    [DataLineageNodeBinding.Logo]: config.logo || null,
    [DataLineageNodeBinding.Status]: config.jobStatus || null,
    [DataLineageNodeBinding.AssetStatus]: item.asset_status,
    [DataLineageNodeBinding.Tags]: item.tags,
    [DataLineageNodeBinding.ValidColumnsCount]: config.validColumnsCount,
    [DataLineageNodeBinding.ItemType]: config.itemType || null,
    [DataLineageNodeBinding.DeletedEntity]: false,
    [DataLineageNodeBinding.HasReadPermission]: !!item.hasReadPermission,
  };
  return node;
};
